import React, { useState, useEffect } from 'react';
import { Sparkles, RefreshCw, CheckCircle, Calendar, Star } from 'lucide-react';
import { useUser } from '../context/UserContext';

const SPARKS = [
  "Reach out to an old friend you haven't spoken to in a year.",
  "Spend 15 minutes in nature without your phone.",
  "Write down three things you are grateful for today.",
  "Do one task slowly and deliberately, focusing only on the process.",
  "Eat a meal until you are only 80% full (Hara Hachi Bu).",
  "Compliment a stranger genuinely.",
  "Learn one new word in a foreign language.",
  "Stretch for 5 minutes.",
  "Listen to someone without interrupting or planning your response.",
  "Fix something small that has been broken for a while.",
  "Cook a meal using fresh ingredients.",
  "Wake up 15 minutes earlier just to enjoy the silence.",
  "Smile at yourself in the mirror.",
  "Donate something you no longer need.",
  "Take a different route to work or the store."
];

const IkigaiSpark: React.FC = () => {
  const [spark, setSpark] = useState<string | null>(null);
  const [isFlipped, setIsFlipped] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { addXp, unlockBadge } = useUser();

  // Check if already done today
  useEffect(() => {
    const today = new Date().toDateString();
    const lastSpark = localStorage.getItem('last_spark_date');
    if (lastSpark === today) {
       const savedSpark = localStorage.getItem('last_spark_text');
       if (savedSpark) {
           setSpark(savedSpark);
           setIsFlipped(true);
           setCompleted(true);
       }
    }
  }, []);

  const revealSpark = () => {
    if (completed) return;
    const randomSpark = SPARKS[Math.floor(Math.random() * SPARKS.length)];
    setSpark(randomSpark);
    setIsFlipped(true);
  };

  const completeSpark = () => {
    if (completed) return;
    setCompleted(true);
    addXp(50, "Completed Daily Ikigai Spark");
    unlockBadge('spark_igniter');
    localStorage.setItem('last_spark_date', new Date().toDateString());
    if (spark) localStorage.setItem('last_spark_text', spark);
  };

  return (
    <div className="max-w-xl mx-auto px-6 pb-20 animate-fadeIn text-center">
      <div className="inline-block p-3 bg-purple-50 text-purple-600 rounded-full mb-4">
        <Sparkles size={32} />
      </div>
      <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Daily Spark</h1>
      <p className="text-nunchi-600 mb-10">Small actions to ignite your purpose.</p>

      <div className="perspective-1000 w-full h-80 relative cursor-pointer" onClick={!isFlipped ? revealSpark : undefined}>
        <div className={`w-full h-full relative preserve-3d transition-transform duration-700 ${isFlipped ? 'rotate-y-180' : ''}`}>
          
          {/* Front of Card */}
          <div className="absolute w-full h-full backface-hidden bg-gradient-to-br from-purple-600 to-rose-500 rounded-2xl shadow-xl flex flex-col items-center justify-center text-white border-4 border-white/20">
             <Star className="w-16 h-16 mb-4 animate-pulse opacity-80" />
             <h2 className="text-2xl font-bold font-serif">Tap to Reveal</h2>
             <p className="text-white/80 mt-2 text-sm">Your mission for today</p>
          </div>

          {/* Back of Card */}
          <div className="absolute w-full h-full backface-hidden rotate-y-180 bg-white rounded-2xl shadow-xl border border-nunchi-100 flex flex-col items-center justify-center p-8">
             <div className="absolute top-4 right-4 text-purple-200">
                <Calendar size={24} />
             </div>
             <h3 className="text-purple-600 font-bold uppercase tracking-widest text-xs mb-4">Today's Mission</h3>
             <p className="text-xl md:text-2xl text-nunchi-800 font-serif font-bold leading-relaxed">
               "{spark}"
             </p>
             
             {!completed ? (
                 <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        completeSpark();
                    }}
                    className="mt-8 px-6 py-2 bg-purple-600 text-white rounded-full font-bold hover:bg-purple-700 transition-colors flex items-center gap-2"
                 >
                    <CheckCircle size={18} /> Mark Complete (+50 XP)
                 </button>
             ) : (
                 <div className="mt-8 flex flex-col items-center gap-2 text-green-600 animate-fadeIn">
                    <CheckCircle size={32} />
                    <span className="font-bold">Mission Accomplished!</span>
                    <span className="text-xs text-nunchi-400">Come back tomorrow.</span>
                 </div>
             )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default IkigaiSpark;